import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Box, Flex, Link, Text, getLinkStylesOn, getTextColorOn, mediaQueries } from 'pcln-design-system'

const TEXT_DARK = 'text.dark'

const CallToActionLink = styled(Link)`
  &&& {
    ${(props) => getLinkStylesOn(props.backgroundColor, props.textColor, TEXT_DARK, true)(props)};
  }
`

const Heading = styled(Text)`
  color: ${(props) => getTextColorOn(props.backgroundColor, props.textColor, TEXT_DARK)(props)};
  letter-spacing: 1px;
`

const HeadingContainer = styled(Flex)`
  white-space: nowrap;

  ${mediaQueries.lg} {
    letter-spacing: 0px;
  }
`

const Subheading = styled(Text)`
  color: ${(props) => getTextColorOn(props.backgroundColor, props.textColor, TEXT_DARK)(props)};
`

const SubheadingContainer = styled(Flex)`
  border-left: unset;

  ${mediaQueries.lg} {
    ${(props) =>
      props.showDivider
        ? `border-left: 1px solid ${getTextColorOn(
            props.backgroundColor,
            props.textColor,
            TEXT_DARK
          )(props)};`
        : ''};
  }
`

const LinkBox = styled(Box)`
  &:hover {
    ${(props) => (props.hasLink ? 'cursor: pointer' : '')}
  }
`

const PromoHeader = ({
  alignText,
  color,
  heading,
  isSubheadingBold,
  linkAriaLabel,
  linkText,
  linkUrl,
  mobileAlignText,
  showDivider,
  subheading,
  textColor,
}) => {
  const backgroundColor = color.includes('.') ? color : `${color}.base`
  const hasLink = Boolean(linkUrl?.length)
  const boxLinkProps =
    !linkText && linkUrl
      ? {
          'aria-role': 'link',
          tabIndex: 0,
        }
      : {}

  function handleClick(linkUrl) {
    // istanbul ignore else
    if (typeof window === 'object' && hasLink) {
      window.open(linkUrl, '_blank')
    }
  }

  function handleKeyPress(event) {
    // istanbul ignore else
    if (event.key === 'Enter') {
      handleClick(linkUrl)
    }
  }

  return (
    <LinkBox
      data-testid='promo-box-link'
      {...boxLinkProps}
      hasLink={hasLink}
      onKeyPress={(e) => handleKeyPress(e)}
      onClick={() => handleClick(linkUrl)}
    >
      <Flex
        alignItems='center'
        color={color}
        flexDirection={['column', null, null, 'row']}
        justifyContent={[mobileAlignText, null, null, alignText]}
        px={[1, null, 0]}
        py={[1, null, 0]}
        textAlign={[mobileAlignText, null, null, alignText]}
      >
        {heading && (
          <HeadingContainer
            justifyContent={[mobileAlignText, null, null, alignText]}
            pb={[0, null, null, 2]}
            pt={[0, null, 2]}
            pl={mobileAlignText === 'left' ? [1, null, 2, 3, 4] : [0, null, 2, 3, 4]}
            pr={showDivider ? [0, null, 2, 3, 4] : [1, null, 2]}
            width={['100%', null, null, 'inherit']}
          >
            <Heading
              backgroundColor={backgroundColor}
              fontSize={0}
              fontWeight='bold'
              pt={[1, null, 0]}
              textColor={textColor}
            >
              {heading}
            </Heading>
          </HeadingContainer>
        )}
        <SubheadingContainer
          backgroundColor={backgroundColor}
          justifyContent={[mobileAlignText, null, null, alignText]}
          pb={heading ? [0, null, 2, 3] : [1, null, 2, 3]}
          pt={heading ? [0, null, 1, 3] : [1, null, 2, 3]}
          px={[0, null, 2, 3, 4]}
          showDivider={!heading ? false : showDivider}
          textColor={textColor}
          width={['100%', null, null, 'inherit']}
        >
          <Subheading
            backgroundColor={backgroundColor}
            fontSize={[0, null, null, 1]}
            fontWeight={isSubheadingBold && 'bold'}
            p={[1, null, 0]}
            textColor={textColor}
          >
            {subheading}
            {linkText && linkUrl && (
              <>
                {' '}
                <CallToActionLink
                  aria-label={linkAriaLabel || linkText}
                  backgroundColor={backgroundColor}
                  href={linkUrl}
                  rel='noopener'
                  target='_blank'
                  textColor={textColor}
                  onClick={(e) => e.preventDefault()}
                >
                  {linkText}
                </CallToActionLink>
              </>
            )}
          </Subheading>
        </SubheadingContainer>
      </Flex>
    </LinkBox>
  )
}

PromoHeader.defaultProps = {
  alignText: 'left',
  color: 'promoPrimary.base',
  isSubheadingBold: true,
  mobileAlignText: 'center',
  showDivider: true,
  textColor: 'text.lightest',
}

PromoHeader.propTypes = {
  alignText: PropTypes.string,
  color: PropTypes.string,
  heading: PropTypes.string,
  isSubheadingBold: PropTypes.bool,
  linkAriaLabel: PropTypes.string,
  linkText: PropTypes.string,
  linkUrl: PropTypes.string,
  mobileAlignText: PropTypes.string,
  showDivider: PropTypes.bool,
  subheading: PropTypes.string,
  textColor: PropTypes.string,
}

export default PromoHeader
